import * as Sentry from '@sentry/react'
import type { User } from '../types/api.types'
import { getAnalyticsUserType } from './analytics/userType'
import { setLogSink, type LogContext } from './logger'

let initialized = false

function applyScopeContext(scope: Sentry.Scope, context?: LogContext): void {
  if (context?.source) {
    scope.setTag('source', context.source)
  }
  if (context?.tags) {
    for (const [key, value] of Object.entries(context.tags)) {
      if (value !== undefined) {
        scope.setTag(key, String(value))
      }
    }
  }
  if (context?.extra) {
    scope.setContext('extra', context.extra)
  }
}

export function isGlitchTipConfigured(): boolean {
  const dsn = import.meta.env.VITE_GLITCHTIP_DSN?.trim()
  return Boolean(dsn)
}

export function isGlitchTipInitialized(): boolean {
  return initialized
}

/** Send a test error to verify GlitchTip wiring (dev / manual QA). */
export function captureGlitchTipTestError(): void {
  if (!isGlitchTipConfigured()) {
    throw new Error(
      'GlitchTip is not configured. Set VITE_GLITCHTIP_DSN in .env and restart the dev server (or rebuild Docker).',
    )
  }
  Sentry.captureException(new Error('QuickComm GlitchTip test error — safe to ignore'))
}

export function initGlitchTip(): void {
  const dsn = import.meta.env.VITE_GLITCHTIP_DSN?.trim()
  if (!dsn) {
    if (import.meta.env.DEV) {
      console.warn(
        '[GlitchTip] Disabled: VITE_GLITCHTIP_DSN is not set. Add it to .env and restart `npm run dev`, or rebuild the Docker image.',
      )
    }
    return
  }

  if (initialized) {
    return
  }

  const tracesSampleRate = Number(
    import.meta.env.VITE_GLITCHTIP_TRACES_SAMPLE_RATE ?? '0.01',
  )
  const isDev = import.meta.env.DEV

  Sentry.init({
    dsn,
    enabled: true,
    environment:
      import.meta.env.VITE_GLITCHTIP_ENVIRONMENT || import.meta.env.MODE || 'development',
    release: import.meta.env.VITE_APP_RELEASE || undefined,
    tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0.01,
    debug: isDev,
    integrations: [Sentry.browserTracingIntegration()],
  })

  initialized = true

  if (isDev) {
    console.info('[GlitchTip] Initialized — test with: window.quickcommGlitchTipTest()')
    ;(window as Window & { quickcommGlitchTipTest?: () => void }).quickcommGlitchTipTest =
      captureGlitchTipTestError
  }

  setLogSink({
    captureError(error, context) {
      Sentry.withScope((scope) => {
        applyScopeContext(scope, context)
        if (error instanceof Error) {
          Sentry.captureException(error)
        } else {
          Sentry.captureException(new Error(String(error)))
        }
      })
      if (import.meta.env.DEV) {
        console.error('[logger:error]', error, context)
      }
    },
    captureMessage(message, context) {
      Sentry.withScope((scope) => {
        applyScopeContext(scope, context)
        Sentry.captureMessage(message, 'warning')
      })
      if (import.meta.env.DEV) {
        console.warn('[logger:message]', message, context)
      }
    },
  })
}

export function setGlitchTipUser(user: User | null): void {
  if (!initialized) {
    return
  }

  if (!user) {
    Sentry.setUser(null)
    return
  }

  Sentry.setUser({
    id: String(user.id),
    username: user.name,
  })
  Sentry.setTag('user_type', getAnalyticsUserType(user))
  Sentry.setTag('role', user.role)
  if (user.organization_id) {
    Sentry.setTag('organization_id', String(user.organization_id))
  }
}

export { Sentry }
